import React from 'react'

interface RadioOptionProps {
  name: string
  value: string
  label: string
  selected: boolean
  onChange: (value: string) => void
  hasOtherField?: boolean
  otherValue?: string
  onOtherChange?: (value: string) => void
}

/**
 * Radio option card for single-choice survey questions.
 *
 * Renders a visually-hidden native <input type="radio"> inside a styled
 * <label> so the entire card is clickable. When selected, shows a purple
 * border and subtle background. When `hasOtherField` is true and the
 * option is selected, an inline text input appears for free-form entry.
 */
const RadioOption: React.FC<RadioOptionProps> = ({
  name,
  value,
  label,
  selected,
  onChange,
  hasOtherField = false,
  otherValue = '',
  onOtherChange,
}) => {
  const inputId = `${name}-${value}`

  return (
    <div>
      <label
        htmlFor={inputId}
        className={`
          flex items-center gap-3 w-full px-4 py-3 rounded-lg border cursor-pointer
          transition-all duration-200
          ${
            selected
              ? 'border-violet-600 bg-violet-50 dark:bg-violet-500/10'
              : 'border-slate-200 dark:border-border bg-background hover:border-slate-300 dark:hover:border-border hover:bg-slate-50 dark:hover:bg-accent'
          }
        `}
      >
        <input
          type="radio"
          id={inputId}
          name={name}
          value={value}
          checked={selected}
          onChange={() => {
            onChange(value)

            // Auto-scroll to next question after selection.
            // Skip when this option has a free-form text field —
            // the user needs to type in "Especifique..." before moving on.
            if (hasOtherField) return

            setTimeout(() => {
              const card = document.getElementById(inputId)?.closest('[data-question]')
              const nextCard = card?.nextElementSibling as HTMLElement | null
              if (nextCard?.hasAttribute('data-question')) {
                const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
                nextCard.scrollIntoView({
                  behavior: prefersReducedMotion ? 'instant' : 'smooth',
                  block: 'center',
                })
              }
            }, 400)
          }}
          className="sr-only"
        />

        {/* Custom radio indicator */}
        <span
          className={`
            flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
            transition-colors duration-200
            ${selected ? 'border-violet-600' : 'border-slate-300 dark:border-border'}
          `}
        >
          {selected && (
            <span className="w-2.5 h-2.5 rounded-full bg-violet-600" />
          )}
        </span>

        <span
          className={`text-sm ${selected ? 'text-slate-900 dark:text-foreground font-medium' : 'text-slate-700 dark:text-muted-foreground'}`}
        >
          {label}
        </span>
      </label>

      {/* "Other" free-form text input, shown when this option is selected */}
      {hasOtherField && selected && (
        <input
          type="text"
          value={otherValue}
          onChange={(e) => onOtherChange?.(e.target.value)}
          placeholder="Especifique..."
          className="mt-2 ml-8 w-[calc(100%-2rem)] px-3 py-2 text-sm border border-slate-200 dark:border-border rounded-lg
            focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent
            placeholder:text-slate-400 dark:placeholder:text-muted-foreground dark:bg-accent dark:text-foreground"
        />
      )}
    </div>
  )
}

export default RadioOption
